import { useEffect, useMemo, useState } from 'react'
import Cookies from 'js-cookie'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import { useUser } from 'src/lib/contexts/UserContext'
import { useOrganisationContext } from 'src/lib/contexts/OrganisationContext'
import { ProductFruits } from 'react-product-fruits'
import Intercom from '@intercom/messenger-js-sdk'
import clarity from 'src/lib/third-party/clarity'
import { constants } from 'src/lib/utils/constants'
import { isEmptyObj } from 'src/lib/utils/helpers'
import Alerts from 'src/components/common/feedback-and-dialog/Alerts'
import { GenericLoader } from 'src/components/common/loader-components/GenericLoader'
import { useMobileContext } from 'src/lib/contexts/MobileContext'

const PRODUCT_FRUITS_WORKSPACE_CODE: string = process.env.NEXT_PUBLIC_APP_PRODUCT_FRUITS_WORKSPACE_CODE ?? ''

const ThirdPartyInitializer = () => {
  const { user } = useUser()
  const {
    getters: { merchant }
  } = useMerchant()
  const {
    getters: { organisationData }
  } = useOrganisationContext()
  const { isMobile } = useMobileContext()
  const intercomToken = Cookies.get(constants.FLO_INTERCOM_TOKEN)

  const [openAlert, setOpenAlert] = useState<string>('')
  const [alertMessage, setAlertMessage] = useState<string>('')

  const handleAlerts = (severity: string, message?: string) => {
    setOpenAlert(severity)
    message && setAlertMessage(message)
  }

  const handleCloseAlert = (_event: any, reason: any) => {
    if (reason === 'clickaway') return
    handleAlerts('')
  }

  useEffect(() => {
    if (!merchant || !user || !organisationData) return

    try {
      Intercom({
        app_id: process.env.NEXT_PUBLIC_APP_INTERCOM_APP_ID ?? '',
        user_id: user.merchantAccountId,
        email: user.email,
        name: `${user.firstName} ${user.lastName}`,
        intercom_user_jwt: intercomToken,
        company_name: organisationData?.name,
        merchant_id: merchant.accountId,
        type: user.role,
        user_role: user.role,
        company: {
          id: merchant.accountId,
          name: organisationData?.name,
          'merchant-id': merchant.accountId,
          'shop-name': merchant.attributes?.shopName,
          website: merchant.attributes?.websiteUrl,
          industry: organisationData?.taxDetails?.category
        },
        hide_default_launcher: isMobile
      })
    } catch (err) {
      console.error('Intercom initialization failed', err)
      handleAlerts('error', 'Failed to initialize Intercom.')
    }
  }, [merchant, user, organisationData])

  useEffect(() => {
    if (!merchant?.accountId) return
    Cookies.set(
      constants.FLO_MERCHANT_DATA,
      JSON.stringify({
        merchantID: merchant?.accountId
      }),
      {
        expires: constants.FLO_USER_COOKIE_EXPIRY,
        sameSite: 'none',
        secure: true
      }
    )
    if (clarity.hasStarted()) {
      clarity.setTag('email', merchant?.email ?? '')
      clarity.setTag('merchant_id', merchant?.accountId ?? '')
    }
  }, [merchant?.accountId])

  const productFruitsUser = useMemo(() => {
    if (!user) return {}

    return {
      username: user.email,
      email: user.email,
      firstname: user.firstName,
      role: user.isAdmin ? 'ADMIN' : 'MEMBER',
      props: {
        merchantId: merchant?.accountId,
        websiteUrl: merchant?.attributes?.websiteUrl,
        shopName: merchant?.attributes?.shopName
      }
    }
  }, [user, merchant])

  return (
    <>
      {openAlert.length > 0 && (
        <Alerts
          open={true}
          duration={2000}
          closeHandler={handleCloseAlert}
          severity={openAlert}
          message={alertMessage}
        />
      )}

      {!isEmptyObj(productFruitsUser) && !user?.isDemoUser && (
        <ProductFruits workspaceCode={PRODUCT_FRUITS_WORKSPACE_CODE} language='en' user={productFruitsUser as any} />
      )}
    </>
  )
}

export default ThirdPartyInitializer
